import {
  Static,
  Intersect,
  String,
  Boolean,
  Object,
  Optional,
  Number,
  Union,
  Literal,
  Array,
  Record,
  Omit,
} from '@sinclair/typebox';

import { CodeName } from '../sas-domain/common';
import { FareRules } from '../sas-domain/fareRules';
import { Coupon } from '../sas-domain/pointsPricing';

import { Cabin } from './cabin';
import { PassengerPrice } from './price';
import { UpsellTable } from './upsellTable';

const Currency = Object({
  code: String(),
});

export type SegmentVia = Static<typeof SegmentVia>;
const SegmentVia = Object({
  code: String(),
  name: String(),
  haltDuration: String(),
  hopperPointTop: Optional(String()),
  hopperId: Optional(String()),
});

export type Segment = Static<typeof Segment>;
export const Segment = Object({
  arrivalTerminal: Optional(String()),
  arrivalDateTimeInGmt: String(),
  arrivalDateTimeInLocal: String(),
  departureTerminal: Optional(String()),
  departureDateTimeInGmt: String(),
  departureDateTimeInLocal: String(),
  departureAirport: CodeName,
  departureCity: CodeName,
  departureCountry: CodeName,
  arrivalAirport: CodeName,
  arrivalCity: CodeName,
  arrivalCountry: CodeName,
  airCraft: CodeName,
  flightNumber: String(),
  duration: String(),
  layoverDuration: Optional(String()),
  marketingCarrier: CodeName,
  operatingCarrier: CodeName,
  iswetlease: Boolean(),
  onTimePerformance: Optional(Number()),
  miles: Optional(Number()),
  numberOfStops: Optional(Number()),
  via: Optional(Array(SegmentVia)),
  segmentId: String(),
  startTimeInLocal: String(),
  endTimeInLocal: String(),
});

export type LowestFare = Static<typeof LowestFare>;
export const LowestFare = Object({
  cabin: String(),
  avlSeats: Optional(Number()),
  product: String(),
  productId: String(),
  selectorId: String(),
  /** @deprecated use priceV2 instead */
  price: Optional(Number()),
  priceV2: Optional(PassengerPrice),
  isStandardAward: Optional(Boolean()),
});

export type Via = Static<typeof Via>;
export const Via = Object({
  code: String(),
  name: Optional(String()),
  haltDuration: String(),
});

export type BoundFlight = Static<typeof BoundFlight>;
export const BoundFlight = Object({
  id: Union([Number(), String()]),
  alternativeFlightId: Optional(String()),
  origin: CodeName,
  originCity: CodeName,
  originCountry: CodeName,
  destination: CodeName,
  destinationCity: CodeName,
  destinationCountry: CodeName,
  startDateTimeInGmt: String(),
  startDateTimeInLocal: String(),
  startTimeInLocal: String(),
  endDateTimeInGmt: String(),
  endDateTimeInLocal: String(),
  endTimeInLocal: String(),
  connectionDuration: String(),
  totalDuration: Optional(String()),
  ranking: Optional(Number()),
  flightKey: Optional(String()),
  segments: Array(Segment),
  stops: Number(),
  lowestFares: Optional(Array(LowestFare)),
  via: Optional(Array(Via)),
  boundType: Optional(Union([Literal('OUTBOUND'), Literal('INBOUND')])),
  cabins: Array(Cabin),
  isSoldOut: Optional(Boolean()),
  isAdditionalPassengerInfoRequired: Optional(Boolean()),
});

export type FlightOffersResponse = Static<typeof FlightOffersResponse>;
export const FlightOffersResponse = Object({
  offerId: String(),
  type: Union([Literal('points'), Literal('revenue')]),
  outboundFlights: Array(BoundFlight),
  inboundFlights: Optional(Array(BoundFlight)),
  upsellTable: Optional(UpsellTable),
  coupons: Optional(Array(Coupon)),
});

export type RebookingOffersResponse = Static<typeof RebookingOffersResponse>;
export const RebookingOffersResponse = Intersect([
  Omit(FlightOffersResponse, ['outboundFlights']),
  Object({
    outboundFlights: Optional(Array(BoundFlight)),
    pricingType: Optional(String()),
    regionName: Optional(String()),
    fareRules: Record(String(), FareRules),
    bookingFlow: String(),
    currency: Optional(Currency),
    commercialFareFamily: Optional(String()),
    codeShareFlights: Array(String()),
  }),
]);
